import { FunctionComponent, memo, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addPackage } from "../../Redux/Slices/Package/AddPackageSlice";
import { EditPackage } from "../../Redux/Slices/Package/EditPackageSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { IPackagelist } from "./IPackage";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import MultiSelect from "../../Components/MultiSelect/MultiSelect";
import {
  Checkbox,
  FormControlLabel,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Box,
  TextField,
} from "@mui/material";
import { Add, Delete, Edit, AddCircleOutline, Check, Close } from "@mui/icons-material";
import { listYearlevel_map } from "../../Redux/Slices/Yearlevel_map/ListYearlevel_mapSlice";
import { listPackageDomain } from "../../Redux/Slices/Package/ListPackageDomainSlice";
import { listPackagePeriod } from "../../Redux/Slices/Package/ListPackagePeriodSlice";

interface PackageFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IPackagelist;
  setInitialValues: React.Dispatch<React.SetStateAction<IPackagelist>>;
}

const PackageForm: FunctionComponent<PackageFormProps> = (props) => {
  const dispatch = useAppDispatch();

  const { data: listExams } = useAppSelector((state) => state.ListCoursesSlice);


  const ExamList = Array.isArray(listExams?.data)
    ? listExams?.data.map((exam) => ({
      label: exam.name!,
      value: exam.id,
    }))
    : [];

  const { data: listYearlevel_maps } = useAppSelector(
    (state) => state.ListYearlevel_mapSlice
  );

  const { data: listDomains } = useAppSelector(
    (state) => state.ListPackageDomainSlice
  );

  const { data: listPeriods } = useAppSelector(
    (state) => state.ListPackagePeriodSlice
  );

  const [featureInput, setFeatureInput] = useState("");
  const [featureStatus, setFeatureStatus] = useState(true);

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      const currentFeatures = methods.getValues("features") || [];
      methods.setValue(
        "features",
        [...currentFeatures, { name: featureInput, status: featureStatus }],
        { shouldValidate: true }
      );
      setFeatureInput("");
      setFeatureStatus(true);
    }
  };

  const handleDeleteFeature = (index: number) => {
    const currentFeatures = methods.getValues("features") || [];
    const newFeatures = currentFeatures.filter((_, i) => i !== index);
    methods.setValue("features", newFeatures, { shouldValidate: true });
  };

  const handleToggleFeature = (index: number) => {
    const currentFeatures = methods.getValues("features") || [];
    const updatedFeatures = currentFeatures.map((feature, i) =>
      i === index ? { ...feature, status: !feature.status } : feature
    );
    methods.setValue("features", updatedFeatures, { shouldValidate: true });
  };

  useEffect(() => {
    dispatch(listYearlevel_map());
    dispatch(listPackageDomain());
    dispatch(listPackagePeriod());
  }, [dispatch]);

  const YearGroupList = Array.isArray(listYearlevel_maps?.data)
    ? listYearlevel_maps?.data.map((map: any) => ({
      label: map.yeargroup?.name!,
      value: map.id,
    }))
    : [];

  const DomainList = Array.isArray(listDomains?.data)
    ? listDomains?.data.map((domain: string) => ({
      label: domain,
      value: domain,
    }))
    : [];

  const BillingTypes = Array.isArray(listPeriods?.data)
    ? listPeriods?.data.map((period: string) => ({
      label: period.charAt(0).toUpperCase() + period.slice(1),
      value: period,
    }))
    : [];
  const userSchema = z
    .object({
      id: z.number().optional(),

      domain: z
        .string({ message: "Please select domain" })
        .min(1, { message: "Please select domain" }),

      name: z
        .string({ message: "Please enter package name" })
        .min(1, { message: "Please enter package name" })
        .max(100, { message: "name cannot exceed 100 characters" }),

      course_ids: z
        .array(z.number({ message: "Please select courses" }), {
          message: "Please select courses",
        })
        .min(1, { message: "Please select courses" }),

      description: z
        .string({ message: "Please enter description" })
        .min(1, { message: "Please enter description" })
        .max(500, { message: "description cannot exceed 500 characters" }),

      cost: z.preprocess(
        (v) => (v === "" || v === null ? undefined : Number(v)),
        z.number({ message: "Please enter cost" })
      ),

      duration: z.preprocess(
        (v) => (v === "" || v === null ? undefined : Number(v)),
        z
          .number({ message: "Please enter duration" })
          .min(1, { message: "Please enter duration" })
      ),

      about: z
        .string({ message: "Please enter package details here" })
        .min(1, { message: "Please enter package details here" })
        .max(500, { message: "about cannot exceed 500 characters" }),

      status: z.boolean({ message: "Please select the status" }),

      admin_only: z.boolean().optional(),

      yeargroup_id: z.preprocess(
        (v) => (v === "" || v === null ? undefined : Number(v)),
        z.number({ message: "Please select year group" })
      ),



      recommended: z.boolean().optional(),

      features: z
        .array(
          z.object({
            name: z.string(),
            status: z.boolean(),
          })
        )
        .optional(),
      billing_type: z
        .string({ message: "Please select billing type" })
        .min(1, { message: "Please select billing type" }),
    });

  const methods = useForm<IPackagelist>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    },

    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset({
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    });
  }, [props.initialValues, methods]);

  const onSubmit: SubmitHandler<IPackagelist> = (val) => {
    if (val.id) {
      dispatch(EditPackage(val));
    } else {
      dispatch(addPackage(val));
    }
  };







  // Status list
  const PackageSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  const { watch, setValue, handleSubmit, reset } = methods;
  const formValues = watch();

  return (
    <FormProvider {...methods}>
      <form ref={props.formRef} onSubmit={handleSubmit(onSubmit)}>
        <div className="form_double_input">
          <Select
            name="domain"
            label="Domain"
            options={DomainList}
            fullWidth
            value={
              DomainList?.find(
                (role) => role.value === formValues.domain
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              setValue("domain", val?.value, { shouldValidate: true });
            }}
          />
          <TextInputFieldC name="name" label="Name" type="text" />
          <MultiSelect
            name="course_ids"
            label="Courses"
            options={ExamList ?? []}
            value={
              ExamList?.filter((list) =>
                formValues.course_ids?.includes(list?.value!)
              ) ?? []
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption[];
              setValue(
                "course_ids",
                val.map((val) => val.value) ?? null,
                {
                  shouldValidate: true,
                }
              );
            }}
          />

          <Select
            name="yeargroup_id"
            label="Year Group"
            options={YearGroupList ?? []}
            value={
              YearGroupList?.find(
                (role) => role.value === formValues.yeargroup_id
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              setValue("yeargroup_id", val?.value, {
                shouldValidate: true,
              });
            }}
          />




          <TextAreaInputField
            name="description"
            label="Description"
            type="text"
            minRows={1}
            maxRows={6}
          />

          <NumberInputField
            name="duration"
            label="Duration in days"
            type="number"
            sx={{ width: "100%" }}
          />

          <NumberInputField name="cost" label="Cost" type="number" />
          <Select
            name="billing_type"
            label="Billing Type"
            options={BillingTypes}
            value={
              BillingTypes?.find(
                (type) => type.value === formValues.billing_type
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              setValue("billing_type", val?.value, {
                shouldValidate: true,
              });
            }}
          />
          <TextAreaInputField
            name="about"
            label="About"
            type="text"
            minRows={1}
            maxRows={6}
          />

          <div style={{ width: "100%", gridColumn: "span 2" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                marginBottom: 2,
              }}
            >
              {/* <Checkbox
                checked={featureStatus}
                onChange={(e) => setFeatureStatus(e.target.checked)}
                color="success"
              /> */}
              <TextField
                label="What's Included"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
              />
              <IconButton onClick={handleAddFeature}>
                <AddCircleOutline
                  sx={{ fontSize: 40, color: "#4785ff", cursor: "pointer" }}
                />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(methods.watch("features") || []).map(
                (feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 16px",
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      transition: "0.2s",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, cursor: "pointer" }} onClick={() => handleToggleFeature(index)}>
                      {feature.status ? (
                        <Check sx={{ color: "#13e31eff", fontSize: 20 }} />
                      ) : (
                        <Close sx={{ color: "#f00f0fff", fontSize: 20 }} />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          textDecoration: feature.status ? "none" : "line-through",
                          color: feature.status ? "text.primary" : "text.disabled",
                          fontSize: "0.95rem",
                        }}
                      >
                        {feature.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => {
                          handleDeleteFeature(index);
                          setFeatureInput(feature.name);
                          setFeatureStatus(feature.status);
                        }}
                        size="small"
                        sx={{ color: "#1976d2", padding: "4px" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteFeature(index)}
                        size="small"
                        sx={{ color: "#d32f2f", padding: "4px" }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </div>
        </div>

        <Select
          sx={{ width: "50%", margin: "20px 0px 0px 0px" }}
          name="status"
          label="Status"
          options={PackageSatus}
          value={
            PackageSatus?.find(
              (role) => role.value === formValues.status
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            setValue("status", val?.value, { shouldValidate: true });
          }}
        />
        <div
          style={{ margin: "20px 0px 10px 0px", display: "flex", gap: "10px" }}
        >
          <div style={{ width: "35%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    marginLeft: "8px",
                    transform: "scale(1.5)", // Scale the size
                  }}
                  checked={formValues.admin_only === true}
                  onChange={() => {
                    setValue(
                      "admin_only",
                      !formValues.admin_only,
                      {
                        shouldValidate: true,
                      }
                    );
                  }}
                />
              }
              label="Admin only"
            />
          </div>
          <div style={{ width: "35%" }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    marginLeft: "8px",
                    transform: "scale(1.5)", // Scale the size
                  }}
                  checked={formValues.recommended === true}
                  onChange={() => {
                    setValue(
                      "recommended",
                      !formValues.recommended,
                      {
                        shouldValidate: true,
                      }
                    );
                  }}
                />
              }
              label="Recommended"
            />
          </div>
          <div style={{ color: "#009dff", fontSize: "15px" }}>
            {" "}
            (Note: If this checkbox is enabled, only the admin can view the
            package. It will not be visible to the student)
          </div>
        </div>
      </form>
    </FormProvider >
  );
};

export default memo(PackageForm);
